(() => {
  const quizRoots = document.querySelectorAll('[data-pdq]');
  if (!quizRoots.length) return;

  quizRoots.forEach((root) => {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';

    const screens = Array.from(root.querySelectorAll('[data-pdq-screen]'));
    const steps = Array.from(root.querySelectorAll('[data-pdq-step]'));
    const progress = root.querySelector('[data-pdq-progress]');
    const progressLabel = root.querySelector('[data-pdq-progress-label]');
    const resultData = JSON.parse(root.querySelector('[data-pdq-products]').textContent);
    const storageKey = `pupadour-scent-quiz-${root.dataset.sectionId}`;
    const state = { answers: {}, dogName: '', step: 0, result: '' };

    const track = (eventName, detail = {}) => {
      const safeDetail = { ...detail };
      delete safeDetail.dogName;
      root.dispatchEvent(new CustomEvent(`pupadour:${eventName}`, { bubbles: true, detail: safeDetail }));
      if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: `pupadour_${eventName}`, ...safeDetail });
    };

    const saveState = () => {
      try { window.sessionStorage.setItem(storageKey, JSON.stringify(state)); } catch (error) { /* Storage is optional. */ }
    };

    const focusHeading = (screen) => {
      const heading = screen.querySelector('h1, h2');
      if (!heading) return;
      heading.setAttribute('tabindex', '-1');
      heading.style.outline = 'none';
      heading.style.border = '0';
      heading.style.boxShadow = 'none';
      window.setTimeout(() => heading.focus({ preventScroll: true }), 30);
    };

    const showScreen = (name, shouldFocus = true, shouldScroll = true) => {
      screens.forEach((screen) => { screen.hidden = screen.dataset.pdqScreen !== name; });
      const active = screens.find((screen) => screen.dataset.pdqScreen === name);
      if (active && shouldFocus) focusHeading(active);
      if (shouldScroll) root.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'start' });
    };

    const showStep = (index) => {
      state.step = Math.max(0, Math.min(index, steps.length - 1));
      steps.forEach((step, stepIndex) => { step.hidden = stepIndex !== state.step; });
      const current = steps[state.step];
      progress.style.width = `${((state.step + 1) / steps.length) * 100}%`;
      progressLabel.textContent = `Step ${state.step + 1} of ${steps.length}`;
      showScreen('quiz', false);
      focusHeading(current);
      saveState();
    };

    const parseScores = (value) => {
      const scores = { seafoam: 0, timber: 0, ember: 0 };
      (value || '').split(',').forEach((pair) => {
        const [key, amount] = pair.split(':');
        if (Object.prototype.hasOwnProperty.call(scores, key)) scores[key] = Number(amount) || 0;
      });
      return scores;
    };

    const calculateResult = () => {
      const totals = { seafoam: 0, timber: 0, ember: 0 };
      Object.values(state.answers).forEach((answer) => {
        Object.entries(answer.scores).forEach(([key, amount]) => { totals[key] += amount; });
      });
      const highest = Math.max(...Object.values(totals));
      const tied = Object.keys(totals).filter((key) => totals[key] === highest);
      if (tied.length === 1) return tied[0];
      const scentPreference = state.answers.scent_scene?.primary;
      if (scentPreference && tied.includes(scentPreference)) return scentPreference;
      const personality = state.answers.personality?.primary;
      if (personality && tied.includes(personality)) return personality;
      return tied[0] || 'seafoam';
    };

    const setEmailTags = (resultKey) => {
      const tagInput = root.querySelector('[data-pdq-tags]');
      if (!tagInput) return;
      const cleanName = state.dogName.replace(/[^a-zA-Z0-9 '\-]/g, '').trim().slice(0, 30);
      const tags = ['newsletter', 'scent-quiz', `scent-${resultKey}`];
      const priority = state.answers.benefit_priority?.answer;
      if (priority) tags.push(`quiz-priority-${priority}`);
      if (cleanName) tags.push(`quiz-dog-name: ${cleanName}`);
      tagInput.value = tags.join(', ');
    };

    const renderResult = (resultKey) => {
      const product = resultData[resultKey];
      if (!product) return;
      state.result = resultKey;
      const dogLabel = state.dogName || 'Your dog';
      const title = root.querySelector('[data-pdq-result-title]');
      const media = root.querySelector('[data-pdq-result-image]');
      const reviewImage = root.querySelector('[data-pdq-review-image]');
      const scentPreference = state.answers.scent_scene?.preference || 'the feeling you want at home';
      const personality = state.answers.personality?.trait || state.answers.perfect_day?.trait || 'unmistakable';
      const benefitCopy = {
        'scent-confidence': 'Every spritz is designed to make close-range cuddles smell noticeably better.',
        'coat-softness': 'Its lightweight conditioning formula also helps leave their coat softer and more pettable.',
        'coat-nourishment': 'Beyond fragrance, its lightweight conditioning formula gives their coat care between baths.',
        'between-bath-ease': 'It is built for the quick reset: shake, spritz, rub, done.'
      }[state.answers.benefit_priority?.answer] || '';
      root.style.setProperty('--pdq-result-color', product.color);
      root.style.setProperty('--pdq-result-wash', product.wash);
      title.textContent = `${dogLabel} is ${product.name}.`;
      root.querySelector('[data-pdq-result-tagline]').textContent = product.tagline;
      root.querySelector('[data-pdq-result-reason]').textContent = `${dogLabel}'s ${personality} energy and your pull toward ${scentPreference} made ${product.name} the strongest match. ${product.reason} ${benefitCopy}`.trim();
      media.src = product.image || '';
      media.alt = `${product.name} coat mist bottle`;
      reviewImage.src = product.reviewImage || product.image || '';
      reviewImage.alt = product.reviewImage ? `Dog from a verified ${product.name} customer review` : `${product.name} coat mist`;
      root.querySelector('[data-pdq-review-quote]').textContent = product.reviewQuote;
      root.querySelector('[data-pdq-review-author]').textContent = `${product.reviewAuthor} · ${product.reviewLocation}`;
      const addButton = root.querySelector('[data-pdq-add]');
      addButton.dataset.variantId = product.variantId || '';
      addButton.disabled = !product.available || !product.variantId;
      addButton.textContent = product.available ? `Add ${product.name} to cart · ${product.price}` : `${product.name} is unavailable`;
      const details = root.querySelector('[data-pdq-details]');
      details.href = product.url;
      details.textContent = `See ${product.name} details`;
      setEmailTags(resultKey);
      showScreen('result');
      saveState();
      track('quiz_complete', { result: resultKey });
    };

    const finishQuiz = () => {
      showScreen('loading');
      const resultKey = calculateResult();
      window.setTimeout(() => renderResult(resultKey), window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 80 : 850);
    };

    root.querySelector('[data-pdq-start]').addEventListener('click', () => {
      showStep(0);
      track('quiz_start', { placement: root.dataset.placement || 'quiz_page' });
    });

    root.querySelectorAll('[data-pdq-choice]').forEach((button) => {
      button.addEventListener('click', () => {
        const step = button.closest('[data-pdq-step]');
        const question = step.dataset.question;
        step.querySelectorAll('[data-pdq-choice]').forEach((choice) => choice.setAttribute('aria-pressed', String(choice === button)));
        state.answers[question] = {
          answer: button.dataset.answer,
          scores: parseScores(button.dataset.scores),
          primary: button.dataset.primary || '',
          trait: button.dataset.trait || '',
          preference: button.dataset.preference || ''
        };
        saveState();
        track('quiz_answer', { question, answer: button.dataset.answer, step: state.step + 1 });
        window.setTimeout(() => state.step === steps.length - 1 ? finishQuiz() : showStep(state.step + 1), 180);
      });
    });

    root.querySelectorAll('[data-pdq-back]').forEach((button) => {
      button.addEventListener('click', () => state.step === 0 ? showScreen('intro') : showStep(state.step - 1));
    });

    const dogInput = root.querySelector('[data-pdq-dog-name]');
    const continueFromName = () => {
      state.dogName = dogInput.value.trim().slice(0, 30);
      saveState();
      track('quiz_personalized', { provided: Boolean(state.dogName) });
      showStep(state.step + 1);
    };
    root.querySelector('[data-pdq-name-continue]').addEventListener('click', continueFromName);
    root.querySelector('[data-pdq-name-skip]').addEventListener('click', () => { dogInput.value = ''; continueFromName(); });
    dogInput.addEventListener('keydown', (event) => { if (event.key === 'Enter') { event.preventDefault(); continueFromName(); } });

    root.querySelector('[data-pdq-retake]').addEventListener('click', () => {
      state.answers = {};
      state.dogName = '';
      state.result = '';
      dogInput.value = '';
      root.querySelectorAll('[data-pdq-choice]').forEach((choice) => choice.setAttribute('aria-pressed', 'false'));
      try { window.sessionStorage.removeItem(storageKey); } catch (error) { /* Storage is optional. */ }
      showStep(0);
      track('quiz_retake');
    });

    root.querySelector('[data-pdq-add]').addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const status = root.querySelector('[data-pdq-cart-status]');
      if (!button.dataset.variantId || button.disabled) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      status.textContent = '';
      const originalText = button.textContent;
      button.textContent = 'Adding to cart…';
      const cartRoot = (window.Shopify?.routes?.root || '/');
      const attributes = {
        'Scent quiz result': resultData[state.result].name,
        'Scent quiz dog name': state.dogName || 'Not provided',
        'Scent quiz priority': state.answers.benefit_priority?.answer || 'Not provided'
      };
      try {
        await fetch(`${cartRoot}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes })
        });
        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = { items: [{ id: Number(button.dataset.variantId), quantity: 1 }], sections_url: window.location.pathname };
        if (cart?.getSectionsToRender) body.sections = cart.getSectionsToRender().map((section) => section.id).join(',');
        const response = await fetch(`${cartRoot}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: JSON.stringify(body)
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || 'This item could not be added.');
        track('quiz_add_to_cart', { result: state.result, variant_id: Number(button.dataset.variantId) });
        if (cart?.renderContents && data.sections) cart.renderContents(data);
        else window.location.href = `${cartRoot}cart`;
        button.textContent = 'Added to cart';
        if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
      } catch (error) {
        status.textContent = error.message || 'Something went wrong. Please try again.';
        button.textContent = originalText;
        button.disabled = false;
      } finally {
        button.removeAttribute('aria-busy');
      }
    });

    const emailForm = root.querySelector('.pdq__email-form');
    if (emailForm) emailForm.addEventListener('submit', () => track('quiz_email_submit', { result: state.result }));

    showScreen('intro', false, false);
  });
})();
